import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import CinematicNav from "@/components/CinematicNav";
import Footer from "@/components/Footer";
import ModuleAccent from "@/components/ModuleAccent";
import { motion } from "framer-motion";
import {
    ArrowLeft, ArrowRight, Check, Sparkles, Building2, Brain,
    Target, MapPin, Network, ShieldCheck, Plus, X, Save, ExternalLink, Lock,
    Wand2, Loader2, Rocket, Copy, MousePointerClick, Code2, FileCheck2, Mic, CreditCard, Crown
} from "lucide-react";

const STEPS = [
    { id: 1, key: "profile",      label: "Profilo",      icon: Building2, hint: "Chi sei, target, USP" },
    { id: 2, key: "knowledge",    label: "Conoscenza",   icon: Brain,     hint: "Servizi, prodotti, FAQ, orari" },
    { id: 3, key: "instructions", label: "Istruzioni",   icon: Sparkles,  hint: "Tono, priorità commerciali" },
    { id: 4, key: "goal",         label: "Obiettivo",    icon: Target,    hint: "Cosa deve fare Aria" },
    { id: 5, key: "geo",          label: "Geo",          icon: MapPin,    hint: "Area operativa" },
    { id: 6, key: "network",      label: "Network",      icon: Network,   hint: "Host First Policy + Aria Network" },
    { id: 7, key: "experience",   label: "Esperienza",   icon: Rocket,    hint: "Livello, voce, installazione" },
];

export default function AriaInstanceConfig() {
    const { token } = useParams();
    const { user, loading: authLoading } = useAuth();
    const nav = useNavigate();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const [saved, setSaved] = useState(false);
    const [cfg, setCfg] = useState(null);
    const [learning, setLearning] = useState(false);
    const [learnMsg, setLearnMsg] = useState("");
    const [activating, setActivating] = useState(null);

    useEffect(() => { document.title = "Configura Aria — TotalKlick"; }, []);

    // Carica config dell'istanza (endpoint authenticato — verifica owner server-side)
    useEffect(() => {
        if (!token) return;
        (async () => {
            try {
                const res = await api.get(`/widget/${token}/owner-config`);
                const c = res.data || {};
                setCfg({
                    persona_name: c.persona_name || "",
                    site_name: c.site_name || "",
                    site_url: c.site_url || "",
                    vertical: c.vertical || "",
                    aria_instance_id: c.aria_instance_id || "",
                    knowledge_base: normalizeKB(c.knowledge_base),
                    custom_instructions: normalizeCI(c.custom_instructions),
                    goal: normalizeGoal(c.goal),
                    geo: normalizeGeo(c.geo),
                    business_profile: normalizeBP(c.business_profile),
                    host_first_policy: normalizeHFP(c.host_first_policy),
                    ecosystem_role: c.ecosystem_role || "host",
                    ecosystem_enabled: !!c.ecosystem_enabled,
                    owner_notes: c.owner_notes || "",
                    plan_tier: c.plan_tier || "free",
                    experience_mode: c.experience_mode || "free",
                    experience_config: c.experience_config || {},
                    last_site_scan_at: c.last_site_scan_at || null,
                    knowledge_files: c.knowledge_files || [],
                    notable_changes: c.notable_changes || [],
                    notable_changes_history: c.notable_changes_history || [],
                });
            } catch (e) {
                setErr(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    const learnFromSite = async () => {
        if (!token || learning) return;
        setLearning(true);
        setLearnMsg("");
        try {
            const res = await api.post(`/widget/${token}/learn-from-site`);
            if (res.data?.knowledge_base) {
                setCfg((p) => ({ ...p, knowledge_base: mergeKbFillOnly(p.knowledge_base, res.data.knowledge_base), last_site_scan_at: new Date().toISOString() }));
            }
            setLearnMsg(res.data?.message || "Scansione completata.");
        } catch (e) {
            setLearnMsg(formatApiError(e));
        } finally {
            setLearning(false);
        }
    };

    const activateMode = async (mode, experienceConfig) => {
        if (!token || activating) return;
        setActivating(mode);
        try {
            const body = experienceConfig ? { mode, experience_config: experienceConfig } : { mode };
            const res = await api.post(`/widget/${token}/activate-mode`, body);
            if (res.data?.upgrade_required) {
                nav(res.data.checkout_path || `/aria/piani/${mode}?widget_token=${token}`);
                return;
            }
            setCfg((p) => ({ ...p, experience_mode: res.data?.experience_mode || mode, experience_config: res.data?.experience_config ?? p.experience_config }));
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setActivating(null);
        }
    };

    const dismissNotableChanges = async () => {
        setCfg((p) => ({ ...p, notable_changes: [] }));
        try { await api.post(`/widget/${token}/notable-changes/ack`); } catch (_e) { /* best-effort */ }
    };

    const [uploadingFile, setUploadingFile] = useState(false);
    const [fileErr, setFileErr] = useState("");

    const uploadKnowledgeFile = async (fileObj) => {
        if (!fileObj || uploadingFile) return;
        setUploadingFile(true);
        setFileErr("");
        try {
            const form = new FormData();
            form.append("file", fileObj);
            const res = await api.post(`/widget/${token}/knowledge-files`, form, { headers: { "Content-Type": "multipart/form-data" } });
            setCfg((p) => ({ ...p, knowledge_files: [...(p.knowledge_files || []), res.data.file] }));
        } catch (e) {
            setFileErr(formatApiError(e));
        } finally {
            setUploadingFile(false);
        }
    };

    const deleteKnowledgeFile = async (fileId) => {
        try {
            await api.delete(`/widget/${token}/knowledge-files/${fileId}`);
            setCfg((p) => ({ ...p, knowledge_files: (p.knowledge_files || []).filter((f) => f.id !== fileId) }));
            setFileErr("");
        } catch (e) {
            setFileErr(formatApiError(e));
        }
    };

    const save = async () => {
        if (!cfg) return;
        // === Validazione campi obbligatori identità (Feb 2026) ===
        const bp = cfg.business_profile || {};
        const missing = [];
        if (!cfg.persona_name?.trim()) missing.push("Persona Aria");
        if (!bp.category) missing.push("Categoria ecosistema");
        if (!bp.sub_category?.trim()) missing.push("Sotto-categoria");
        if (!bp.price_range) missing.push("Fascia prezzo");
        if (!bp.target_audience?.trim()) missing.push("Target di riferimento");
        if (!bp.unique_value_proposition?.trim()) missing.push("USP");
        if (!bp.phone?.trim()) {
            missing.push("Telefono");
        } else if (!/^\+\d{1,3}[\s\-]?\d{6,14}$/.test(bp.phone.replace(/\s+/g, " ").trim())) {
            setStep(1);
            setErr("Telefono non valido. Usa formato internazionale (+39 333 1234567).");
            return;
        }
        if (missing.length) {
            setStep(1);
            setErr(`Compila i campi obbligatori: ${missing.join(", ")}`);
            return;
        }
        setSaving(true); setErr(""); setSaved(false);
        try {
            await api.patch(`/widget/${token}`, {
                persona_name: cfg.persona_name || null,
                knowledge_base: cfg.knowledge_base,
                custom_instructions: cfg.custom_instructions,
                goal: cfg.goal,
                geo: cfg.geo,
                business_profile: cfg.business_profile,
                host_first_policy: cfg.host_first_policy,
                ecosystem_role: cfg.ecosystem_role,
                ecosystem_enabled: cfg.ecosystem_enabled,
                owner_notes: cfg.owner_notes || "",
            });
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const upd = (k, v) => setCfg((p) => ({ ...p, [k]: v }));
    const updNested = (parent, k, v) => setCfg((p) => ({ ...p, [parent]: { ...(p[parent] || {}), [k]: v } }));

    if (authLoading || loading) {
        return (
            <ModuleAccent module="aria" as="div" className="min-h-screen" style={baseStyle}>
                <CinematicNav />
                <div className="max-w-md mx-auto pt-40 px-5 text-center text-white/60" style={{ fontFamily: "Geist, sans-serif" }}>
                    Caricamento istanza Aria…
                </div>
            </ModuleAccent>
        );
    }

    if (!cfg) {
        const is403 = err && /proprietario|403|forbid/i.test(err);
        return (
            <ModuleAccent module="aria" as="div" className="min-h-screen" style={baseStyle}>
                <CinematicNav />
                <div className="max-w-md mx-auto pt-40 px-5 text-center">
                    <div className="text-white text-[22px] mb-3" style={{ fontFamily: "Geist, sans-serif", fontWeight: 300 }}>
                        {is403 ? "Accesso negato" : "Istanza non trovata"}
                    </div>
                    <p className="text-white/55 mb-6">{err || "Token non valido o assistente disattivato."}</p>
                    <Link to="/aria-widget" className="eco-btn-primary">Torna ad Aria</Link>
                </div>
            </ModuleAccent>
        );
    }

    return (
        <ModuleAccent module="aria" as="div" className="min-h-screen w-full overflow-x-hidden" style={baseStyle} data-testid="aria-config-page">
            <CinematicNav />

            <section className="px-5 md:px-10 pt-28 pb-6 max-w-6xl mx-auto">
                <div className="flex items-center gap-2 mb-4 text-[11px] uppercase tracking-[0.22em] text-white/45" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                    <Link to="/aria-widget" className="hover:text-white inline-flex items-center gap-1.5"><ArrowLeft size={11} /> Aria</Link>
                    <span>/</span>
                    <span className="text-white/70">Istanza · {cfg.aria_instance_id || token.slice(0, 8)}</span>
                </div>
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <h1 className="text-white tracking-tight" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(1.8rem,3.6vw,2.8rem)", lineHeight: 1.05, letterSpacing: "-0.03em" }}>
                            Configura <span style={{ fontWeight: 600 }}>{cfg.persona_name || `Aria ${cfg.vertical}`}</span>
                        </h1>
                        <p className="text-white/55 mt-1 text-[14px]" style={{ fontFamily: "Outfit, sans-serif" }}>
                            Istanza Aria per <b className="text-white/80">{cfg.site_name}</b> · {cfg.site_url}
                        </p>
                    </div>
                    <a
                        href={`/aria-embed?token=${token}&preview=1`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[13px] text-white/85 hover:text-white transition"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}
                        data-testid="aria-config-preview"
                    >
                        Anteprima chat <ExternalLink size={13} />
                    </a>
                </div>
            </section>

            {cfg.notable_changes?.length > 0 && (
                <section className="px-5 md:px-10 max-w-6xl mx-auto -mt-2 mb-4">
                    <div className="rounded-2xl p-4 flex items-start justify-between gap-4 flex-wrap" style={{ background: "rgba(16,185,129,0.07)", border: "1px solid rgba(16,185,129,0.28)" }} data-testid="notable-changes-banner">
                        <div className="flex items-start gap-2.5">
                            <Sparkles size={15} className="text-emerald-300 mt-0.5 flex-none" />
                            <div>
                                <div className="text-emerald-300 text-[12px] mb-1.5" style={{ fontFamily: "JetBrains Mono, monospace", textTransform: "uppercase", letterSpacing: "0.16em" }}>
                                    Novità trovate dalla scansione automatica
                                </div>
                                <ul className="space-y-1">
                                    {cfg.notable_changes.map((c) => (
                                        <li key={c.id} className="text-white/75 text-[12.5px]" style={{ fontFamily: "Outfit, sans-serif" }}>{c.summary}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <button type="button" onClick={dismissNotableChanges} className="text-[11.5px] text-white/50 hover:text-white transition flex-none" data-testid="notable-changes-dismiss">
                            Ho visto, chiudi
                        </button>
                    </div>
                </section>
            )}

            <section className="px-5 md:px-10 pb-24 max-w-6xl mx-auto grid lg:grid-cols-4 gap-6">
                {/* SIDEBAR steps */}
                <aside className="lg:col-span-1">
                    <ol className="space-y-1.5 sticky top-28">
                        {STEPS.map((s) => {
                            const active = step === s.id;
                            const done = step > s.id;
                            const Icon = s.icon;
                            return (
                                <li key={s.id}>
                                    <button
                                        type="button"
                                        onClick={() => setStep(s.id)}
                                        className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition"
                                        style={{
                                            background: active ? "var(--mod-accent-soft)" : "rgba(15,18,36,0.45)",
                                            border: `1px solid ${active ? "var(--mod-accent)" : "rgba(255,255,255,0.06)"}`,
                                        }}
                                        data-testid={`aria-step-${s.key}`}
                                    >
                                        <span className="w-7 h-7 rounded-lg grid place-items-center flex-none" style={{ background: done || active ? "var(--mod-accent-soft)" : "rgba(255,255,255,0.04)", color: done || active ? "var(--mod-accent)" : "rgba(255,255,255,0.5)" }}>
                                            {done ? <Check size={13} /> : <Icon size={13} />}
                                        </span>
                                        <span>
                                            <span className="block text-[13px] text-white/90" style={{ fontFamily: "Geist, sans-serif", fontWeight: active ? 500 : 400 }}>{s.label}</span>
                                            <span className="block text-[11px] text-white/40" style={{ fontFamily: "Outfit, sans-serif" }}>{s.hint}</span>
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                    </ol>
                </aside>

                {/* PANEL */}
                <div className="lg:col-span-3 rounded-3xl p-6 md:p-8" style={panelStyle}>
                    {err && <ErrBanner msg={err} />}
                    {step === 1 && <StepProfile cfg={cfg} upd={upd} updNested={updNested} />}
                    {step === 2 && (
                        <StepKnowledge
                            cfg={cfg} upd={upd} updNested={updNested}
                            onLearnFromSite={learnFromSite} learning={learning} learnMsg={learnMsg}
                            onUploadFile={uploadKnowledgeFile} uploadingFile={uploadingFile} fileErr={fileErr} onDeleteFile={deleteKnowledgeFile}
                        />
                    )}
                    {step === 3 && <StepInstructions cfg={cfg} updNested={updNested} />}
                    {step === 4 && <StepGoal cfg={cfg} updNested={updNested} />}
                    {step === 5 && <StepGeo cfg={cfg} updNested={updNested} />}
                    {step === 6 && <StepNetwork cfg={cfg} upd={upd} updNested={updNested} isSuperadmin={user?.role === "superadmin"} />}
                    {step === 7 && <StepExperience cfg={cfg} token={token} onActivate={activateMode} activating={activating} />}

                    {/* Footer nav */}
                    <div className="flex items-center justify-between gap-3 mt-8 pt-5 border-t border-white/5">
                        <button
                            type="button"
                            disabled={step === 1}
                            onClick={() => setStep((s) => Math.max(1, s - 1))}
                            className="eco-btn-ghost disabled:opacity-30"
                            data-testid="aria-step-prev"
                        >
                            <ArrowLeft size={14} /> Indietro
                        </button>
                        <div className="flex items-center gap-2.5">
                            <button
                                type="button"
                                onClick={save}
                                disabled={saving}
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[13px] text-white transition hover:scale-[1.02] disabled:opacity-50"
                                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}
                                data-testid="aria-step-save"
                            >
                                {saving ? "Salvataggio…" : saved ? <><Check size={13} /> Salvato</> : <><Save size={13} /> Salva modifiche</>}
                            </button>
                            {step < STEPS.length ? (
                                <button
                                    type="button"
                                    onClick={() => setStep((s) => Math.min(STEPS.length, s + 1))}
                                    className="eco-btn-primary"
                                    data-testid="aria-step-next"
                                >
                                    Avanti <ArrowRight size={14} />
                                </button>
                            ) : (
                                <Link
                                    to={`/aria-embed?token=${token}&preview=1`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="eco-btn-primary"
                                    data-testid="aria-step-launch"
                                >
                                    Lancia anteprima <ArrowRight size={14} />
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <Footer variant="hub" />
            <style>{inputCSS}</style>
        </ModuleAccent>
    );
}

// ============== STEPS ==============

function StepProfile({ cfg, upd, updNested }) {
    const bp = cfg.business_profile || {};
    return (
        <>
            <StepHeader n="01" title="Profilo business" subtitle="La tua identità — tutti i campi sono obbligatori. Aria li userà per parlare come te." />
            <div className="grid md:grid-cols-2 gap-4 mt-5">
                <Field label="Persona Aria" required>
                    <input className="eco-input" required value={cfg.persona_name || ""} onChange={(e) => upd("persona_name", e.target.value)} placeholder={`Aria ${cfg.vertical}`} data-testid="cfg-persona" />
                </Field>
                <Field label="Categoria ecosistema" required>
                    <select className="eco-input" required value={bp.category || ""} onChange={(e) => updNested("business_profile", "category", e.target.value)} data-testid="cfg-bp-category">
                        <option value="">— scegli —</option>
                        <option value="food">Food & Beverage</option>
                        <option value="fitness">Fitness & Wellness</option>
                        <option value="print">Stampa & Tipografia</option>
                        <option value="retail">Retail / Negozio</option>
                        <option value="services">Servizi professionali</option>
                        <option value="hospitality">Hospitality</option>
                        <option value="beauty">Beauty</option>
                        <option value="education">Education</option>
                        <option value="health">Salute</option>
                        <option value="ecommerce">E-commerce</option>
                        <option value="other">Altro</option>
                    </select>
                </Field>
                <Field label="Sotto-categoria (specifica)" required>
                    <input className="eco-input" required value={bp.sub_category || ""} onChange={(e) => updNested("business_profile", "sub_category", e.target.value)} placeholder="es. pizzeria_napoletana" data-testid="cfg-bp-subcat" />
                </Field>
                <Field label="Fascia prezzo" required>
                    <select className="eco-input" required value={bp.price_range || ""} onChange={(e) => updNested("business_profile", "price_range", e.target.value)} data-testid="cfg-bp-price">
                        <option value="">— scegli —</option>
                        <option value="economico">Economico</option>
                        <option value="medio">Medio</option>
                        <option value="alto">Alto</option>
                        <option value="luxury">Luxury</option>
                    </select>
                </Field>
                <Field label="Telefono titolare (con prefisso internazionale)" required full>
                    <input className="eco-input" required type="tel" pattern="^\+\d{1,3}[\s\-]?\d{6,14}$" value={bp.phone || ""} onChange={(e) => updNested("business_profile", "phone", e.target.value)} placeholder="+39 333 1234567" data-testid="cfg-bp-phone" />
                    <span className="block text-[11px] text-white/40 mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>Formato internazionale obbligatorio (+39 …). Usato solo per ricontatti diretti.</span>
                </Field>
                <Field label="Target di riferimento" required full>
                    <input className="eco-input" required value={bp.target_audience || ""} onChange={(e) => updNested("business_profile", "target_audience", e.target.value)} placeholder="es. Famiglie 30-55, residenti zona" data-testid="cfg-bp-target" />
                </Field>
                <Field label="USP — cosa ti rende unico" required full>
                    <textarea className="eco-input min-h-[80px]" required value={bp.unique_value_proposition || ""} onChange={(e) => updNested("business_profile", "unique_value_proposition", e.target.value)} placeholder="In una frase: cosa fa la differenza" data-testid="cfg-bp-usp" />
                </Field>
                <ChipList label="Competitor da NON nominare mai" items={bp.competitors_to_avoid || []} onChange={(arr) => updNested("business_profile", "competitors_to_avoid", arr)} placeholder="Nome competitor" testid="cfg-bp-competitors" />
            </div>
        </>
    );
}

function StepKnowledge({ cfg, upd, updNested, onLearnFromSite, learning, learnMsg, onUploadFile, uploadingFile, fileErr, onDeleteFile }) {
    const kb = cfg.knowledge_base || {};
    const [showHistory, setShowHistory] = useState(false);
    const updItemList = (key) => (arr) => updNested("knowledge_base", key, arr);
    return (
        <>
            <StepHeader n="02" title="Knowledge Base" subtitle="Cosa Aria deve sapere su di te. Niente prezzi inventati." />

            <div className="rounded-2xl p-4 mt-5 mb-1" style={{ background: "rgba(168,85,247,0.06)", border: "1px solid rgba(168,85,247,0.22)" }} data-testid="cfg-learn-site-box">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <div className="flex items-center gap-2 text-[#C9A6FF] text-[12px] mb-1" style={{ fontFamily: "JetBrains Mono, monospace", textTransform: "uppercase", letterSpacing: "0.18em" }}>
                            <Wand2 size={13} /> Compila dal sito
                        </div>
                        <p className="text-white/70 text-[12.5px] leading-relaxed max-w-md" style={{ fontFamily: "Outfit, sans-serif" }}>
                            Leggo le pagine principali di <b className="text-white">{cfg.site_url || "il tuo sito"}</b> e riempio solo i campi ancora vuoti qui sotto — non tocco mai ciò che hai già scritto a mano.
                        </p>
                        {cfg.last_site_scan_at && (
                            <p className="text-white/40 text-[11px] mt-1" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                Ultima scansione: {new Date(cfg.last_site_scan_at).toLocaleString("it-IT")} · si ripete da sola periodicamente
                            </p>
                        )}
                        {cfg.notable_changes_history?.length > 0 && (
                            <button type="button" onClick={() => setShowHistory((v) => !v)} className="text-[#C9A6FF]/80 hover:text-[#C9A6FF] text-[11px] mt-1.5 underline underline-offset-2" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="cfg-history-toggle">
                                {showHistory ? "Nascondi" : "Vedi"} cronologia novità ({cfg.notable_changes_history.length})
                            </button>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onLearnFromSite}
                        disabled={learning}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-white text-[13px] transition hover:scale-[1.02] disabled:opacity-60 flex-none"
                        style={{ background: "linear-gradient(135deg, #A855F7, #7C3AED)", fontFamily: "Geist, sans-serif", fontWeight: 500, boxShadow: "0 14px 36px -12px rgba(168,85,247,0.55)" }}
                        data-testid="cfg-learn-site-btn"
                    >
                        {learning ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
                        {learning ? "Leggo il sito…" : "Compila dal sito"}
                    </button>
                </div>
                {learnMsg && (
                    <div className="mt-3 px-3 py-2 rounded-lg text-[12px] text-white/85" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="cfg-learn-site-result">
                        {learnMsg}
                    </div>
                )}
                {showHistory && (
                    <ul className="mt-3 space-y-1.5" data-testid="cfg-history-list">
                        {cfg.notable_changes_history.map((c) => (
                            <li key={c.id} className="text-white/55 text-[11.5px] px-3 py-1.5 rounded-lg flex items-start gap-2" style={{ background: "rgba(255,255,255,0.03)" }}>
                                <span className="text-white/30 flex-none" style={{ fontFamily: "JetBrains Mono, monospace" }}>{new Date(c.at).toLocaleDateString("it-IT")}</span>
                                <span>{c.summary}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-5">
                <Field label="Chi siamo (1-3 frasi)" full>
                    <textarea className="eco-input min-h-[80px]" value={kb.company_description || ""} onChange={(e) => updNested("knowledge_base", "company_description", e.target.value)} placeholder="Descrizione breve dell'attività" data-testid="cfg-kb-desc" />
                </Field>
                <Field label="Orari">
                    <input className="eco-input" value={kb.opening_hours || ""} onChange={(e) => updNested("knowledge_base", "opening_hours", e.target.value)} placeholder="lun-ven 9-19, sab 9-13" data-testid="cfg-kb-hours" />
                </Field>
                <Field label="Telefono / WhatsApp">
                    <input className="eco-input" value={(kb.contacts && kb.contacts.phone) || ""} onChange={(e) => updNested("knowledge_base", "contacts", { ...(kb.contacts || {}), phone: e.target.value })} placeholder="+39 ..." data-testid="cfg-kb-phone" />
                </Field>
                <Field label="Email">
                    <input className="eco-input" value={(kb.contacts && kb.contacts.email) || ""} onChange={(e) => updNested("knowledge_base", "contacts", { ...(kb.contacts || {}), email: e.target.value })} placeholder="info@..." data-testid="cfg-kb-email" />
                </Field>
                <NameDescriptionList label="Servizi" items={kb.services || []} onChange={updItemList("services")} testid="cfg-kb-services" />
                <NameDescriptionList label="Prodotti" items={kb.products || []} onChange={updItemList("products")} testid="cfg-kb-products" />
                <FaqList label="FAQ" items={kb.faq || []} onChange={updItemList("faq")} testid="cfg-kb-faq" full />
                <ChipList label="Keyword di settore" items={kb.keywords || []} onChange={updItemList("keywords")} placeholder="parola chiave" testid="cfg-kb-keywords" full />
                <Field label="Note private (solo tu le vedi — Aria le usa in chat)" full>
                    <textarea className="eco-input min-h-[70px]" value={cfg.owner_notes || ""} onChange={(e) => upd("owner_notes", e.target.value)} placeholder="es. il sabato c'è sconto 10% a voce, non scritto sul sito" data-testid="cfg-owner-notes" />
                </Field>
            </div>

            <div className="rounded-2xl p-4 mt-5" style={{ background: "rgba(79,139,255,0.05)", border: "1px solid rgba(79,139,255,0.18)" }} data-testid="cfg-knowledge-files-box">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                    <div>
                        <div className="flex items-center gap-2 text-[#9EC3FF] text-[12px] mb-1" style={{ fontFamily: "JetBrains Mono, monospace", textTransform: "uppercase", letterSpacing: "0.18em" }}>
                            <FileCheck2 size={13} /> Documenti aggiuntivi (PDF / TXT)
                        </div>
                        <p className="text-white/60 text-[12px] leading-relaxed max-w-md" style={{ fontFamily: "Outfit, sans-serif" }}>
                            Carica listini, cataloghi o regolamenti — Aria li legge e li usa nelle risposte. Max 5 file, 5 MB l'uno.
                        </p>
                    </div>
                    <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-white text-[13px] transition hover:scale-[1.02] cursor-pointer flex-none" style={{ background: "linear-gradient(135deg, #4F8BFF, #2B5AFF)", fontFamily: "Geist, sans-serif", fontWeight: 500, opacity: uploadingFile ? 0.6 : 1 }} data-testid="cfg-kfile-upload-btn">
                        {uploadingFile ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                        {uploadingFile ? "Carico…" : "Carica documento"}
                        <input
                            type="file"
                            accept=".pdf,.txt"
                            className="hidden"
                            disabled={uploadingFile}
                            onChange={(e) => { if (e.target.files?.[0]) onUploadFile(e.target.files[0]); e.target.value = ""; }}
                            data-testid="cfg-kfile-input"
                        />
                    </label>
                </div>
                {fileErr && <p className="text-rose-400 text-[12px] mb-2" data-testid="cfg-kfile-error">{fileErr}</p>}
                {(cfg.knowledge_files || []).length === 0 ? (
                    <p className="text-white/35 text-[12px]">Nessun documento caricato.</p>
                ) : (
                    <ul className="space-y-1.5">
                        {(cfg.knowledge_files || []).map((f) => (
                            <li key={f.id} className="flex flex-col gap-1 px-3 py-2 rounded-lg" style={{ background: "rgba(255,255,255,0.03)" }} data-testid={`cfg-kfile-${f.id}`}>
                                <div className="flex items-center justify-between gap-3">
                                    <span className="text-white/80 text-[12.5px] truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{f.filename} <span className="text-white/35 text-[11px]">· {f.char_count} caratteri</span></span>
                                    <button type="button" onClick={() => onDeleteFile(f.id)} className="text-white/40 hover:text-rose-400 transition flex-none" data-testid={`cfg-kfile-delete-${f.id}`}>
                                        <X size={14} />
                                    </button>
                                </div>
                                {f.summary && <p className="text-[#9EC3FF]/80 text-[11.5px] italic" style={{ fontFamily: "Outfit, sans-serif" }}>"{f.summary}"</p>}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

        </>
    );
}

function StepInstructions({ cfg, updNested }) {
    const ci = cfg.custom_instructions || {};
    return (
        <>
            <StepHeader n="03" title="Istruzioni operative" subtitle="Come Aria deve parlare e cosa spingere." />
            <div className="grid md:grid-cols-2 gap-4 mt-5">
                <Field label="Tono di voce">
                    <select className="eco-input" value={ci.tone_of_voice || "professionale"} onChange={(e) => updNested("custom_instructions", "tone_of_voice", e.target.value)} data-testid="cfg-ci-tone">
                        <option value="professionale">Professionale</option>
                        <option value="amichevole">Amichevole</option>
                        <option value="diretto">Diretto</option>
                        <option value="premium">Premium / Lusso</option>
                    </select>
                </Field>
                <Field label="Zona operativa">
                    <input className="eco-input" value={ci.operating_area || ""} onChange={(e) => updNested("custom_instructions", "operating_area", e.target.value)} placeholder="es. Roma centro · Trastevere" data-testid="cfg-ci-area" />
                </Field>
                <ChipList label="Da promuovere (priority products)" items={ci.priority_products || []} onChange={(arr) => updNested("custom_instructions", "priority_products", arr)} placeholder="Nome prodotto" testid="cfg-ci-priority" />
                <ChipList label="Da evitare (avoid products)" items={ci.avoid_products || []} onChange={(arr) => updNested("custom_instructions", "avoid_products", arr)} placeholder="Nome prodotto" testid="cfg-ci-avoid" />
                <Field label="Priorità commerciali (note libere)" full>
                    <textarea className="eco-input min-h-[80px]" value={ci.commercial_priorities || ""} onChange={(e) => updNested("custom_instructions", "commercial_priorities", e.target.value)} placeholder="Spingere annuale invece di mensile, up-sell PT dopo 1 mese..." data-testid="cfg-ci-priorities" />
                </Field>
                <ChipList label="Domande obbligatorie" items={ci.must_ask || []} onChange={(arr) => updNested("custom_instructions", "must_ask", arr)} placeholder="Domanda" testid="cfg-ci-must-ask" />
                <ChipList label="Argomenti vietati" items={ci.must_avoid || []} onChange={(arr) => updNested("custom_instructions", "must_avoid", arr)} placeholder="Argomento" testid="cfg-ci-must-avoid" />
            </div>
        </>
    );
}

function StepGoal({ cfg, updNested }) {
    const g = cfg.goal || {};
    return (
        <>
            <StepHeader n="04" title="Obiettivo Aria" subtitle="Cosa deve fare per il sito ospitante. Definisci criteri di successo." />
            <div className="grid md:grid-cols-2 gap-4 mt-5">
                <Field label="Tipo di goal">
                    <select className="eco-input" value={g.type || "lead"} onChange={(e) => updNested("goal", "type", e.target.value)} data-testid="cfg-goal-type">
                        <option value="lead">Lead generation</option>
                        <option value="booking">Prenotazione</option>
                        <option value="quote">Richiesta preventivo</option>
                        <option value="purchase">Acquisto</option>
                        <option value="signup">Registrazione</option>
                        <option value="consultation">Consulenza</option>
                        <option value="table">Tavolo ristorante</option>
                        <option value="info">Informazione</option>
                        <option value="other">Altro</option>
                    </select>
                </Field>
                <Field label="CTA label">
                    <input className="eco-input" value={g.cta_label || ""} onChange={(e) => updNested("goal", "cta_label", e.target.value)} placeholder="Prenota una prova gratuita" data-testid="cfg-goal-cta-label" />
                </Field>
                <Field label="CTA URL (opzionale, deep-link esterno)" full>
                    <input className="eco-input" value={g.cta_url || ""} onChange={(e) => updNested("goal", "cta_url", e.target.value)} placeholder="https://tuosito.it/prenota" data-testid="cfg-goal-cta-url" />
                </Field>
                <Field label="Descrizione del goal" full>
                    <textarea className="eco-input min-h-[70px]" value={g.description || ""} onChange={(e) => updNested("goal", "description", e.target.value)} placeholder="es. Prenotare un tavolo per gruppi 4+ persone" data-testid="cfg-goal-desc" />
                </Field>
                <ChipList label="Criteri di completamento (cosa raccogliere)" items={g.completion_criteria || []} onChange={(arr) => updNested("goal", "completion_criteria", arr)} placeholder="es. email_raccolta" testid="cfg-goal-criteria" />
                <ChipList label="Segnali di successo (frasi utente)" items={g.success_value_signals || []} onChange={(arr) => updNested("goal", "success_value_signals", arr)} placeholder='es. "voglio prenotare"' testid="cfg-goal-signals" />
                <Field label="Fallback se fallisce">
                    <select className="eco-input" value={g.fallback_action || "lead_capture"} onChange={(e) => updNested("goal", "fallback_action", e.target.value)} data-testid="cfg-goal-fallback">
                        <option value="lead_capture">Cattura lead</option>
                        <option value="info">Solo informazione</option>
                        <option value="escalate">Escalate a operatore</option>
                    </select>
                </Field>
                <Field label="Messaggio di successo" full>
                    <input className="eco-input" value={g.success_message || ""} onChange={(e) => updNested("goal", "success_message", e.target.value)} placeholder="Perfetto, ti chiamiamo a breve!" data-testid="cfg-goal-success" />
                </Field>
            </div>
        </>
    );
}

function StepGeo({ cfg, updNested }) {
    const g = cfg.geo || {};
    return (
        <>
            <StepHeader n="05" title="Area operativa" subtitle="Geografia del sito. Usata per scoping risposte e — in futuro — Network matching." />
            <div className="grid md:grid-cols-2 gap-4 mt-5">
                <Field label="Città">
                    <input className="eco-input" value={g.city || ""} onChange={(e) => updNested("geo", "city", e.target.value)} placeholder="Roma" data-testid="cfg-geo-city" />
                </Field>
                <Field label="Provincia">
                    <input className="eco-input" value={g.province || ""} onChange={(e) => updNested("geo", "province", e.target.value)} placeholder="RM" data-testid="cfg-geo-province" />
                </Field>
                <Field label="Regione">
                    <input className="eco-input" value={g.region || ""} onChange={(e) => updNested("geo", "region", e.target.value)} placeholder="Lazio" data-testid="cfg-geo-region" />
                </Field>
                <Field label="Paese">
                    <input className="eco-input" value={g.country || "IT"} onChange={(e) => updNested("geo", "country", e.target.value)} placeholder="IT" data-testid="cfg-geo-country" />
                </Field>
                <Field label="Raggio operativo (km)">
                    <input type="number" className="eco-input" value={g.radius_km || 10} onChange={(e) => updNested("geo", "radius_km", parseInt(e.target.value || "10", 10))} data-testid="cfg-geo-radius" />
                </Field>
                <Field label="Timezone">
                    <input className="eco-input" value={g.timezone || "Europe/Rome"} onChange={(e) => updNested("geo", "timezone", e.target.value)} data-testid="cfg-geo-tz" />
                </Field>
            </div>
        </>
    );
}

function StepNetwork({ cfg, upd, updNested, isSuperadmin }) {
    const hfp = cfg.host_first_policy || {};
    const locked = !isSuperadmin;
    return (
        <>
            <StepHeader n="06" title="Host First Policy & Network" subtitle="Aria serve PRIMA il tuo sito. Solo dopo aver raggiunto il goal può proporre partner Aria." />

            {locked ? (
                <div className="rounded-2xl p-4 mt-5 mb-5" style={{ background: "rgba(255,170,40,0.06)", border: "1px solid rgba(255,170,40,0.25)" }} data-testid="cfg-network-locked-banner">
                    <div className="flex items-center gap-2 text-[#FFC36B] text-[12px] mb-2" style={{ fontFamily: "JetBrains Mono, monospace", textTransform: "uppercase", letterSpacing: "0.18em" }}>
                        <Lock size={13} /> Impostazioni gestite da TotalKlick
                    </div>
                    <p className="text-white/80 text-[13px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                        Le opzioni della sezione <strong>Network</strong> sono bloccate per garantire l&apos;integrità
                        della <em>Host First Policy</em>. Per richiedere modifiche su questa specifica istanza Aria,
                        scrivi a <a href="mailto:supporto@totalklick.it" className="text-[#FFC36B] underline">supporto@totalklick.it</a>:
                        valuteremo la richiesta e applicheremo le modifiche dalla dashboard TotalKlick.
                    </p>
                </div>
            ) : (
                <div className="rounded-2xl p-4 mt-5 mb-5" style={{ background: "rgba(255,26,92,0.08)", border: "1px solid rgba(255,26,92,0.3)" }} data-testid="cfg-network-admin-banner">
                    <div className="flex items-center gap-2 text-[#FF1A5C] text-[12px] mb-2" style={{ fontFamily: "JetBrains Mono, monospace", textTransform: "uppercase", letterSpacing: "0.18em" }}>
                        <ShieldCheck size={13} /> Admin override · TotalKlick
                    </div>
                    <p className="text-white/80 text-[13px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                        Stai modificando il Network di questa istanza come <strong>superadmin</strong>. Le modifiche
                        salveranno solo dopo aver premuto <em>Salva</em> in fondo alla pagina.
                    </p>
                </div>
            )}

            <div className="rounded-2xl p-4 mb-5" style={{ background: "rgba(79,139,255,0.06)", border: "1px solid rgba(79,139,255,0.2)" }}>
                <div className="flex items-center gap-2 text-[#9EC3FF] text-[12px] mb-2" style={{ fontFamily: "JetBrains Mono, monospace", textTransform: "uppercase", letterSpacing: "0.18em" }}>
                    <ShieldCheck size={13} /> Regola d&apos;oro Aria
                </div>
                <p className="text-white/75 text-[13px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                    L&apos;istanza Aria non proporrà mai partner esterni, brand o aziende del Network finché
                    l&apos;obiettivo del sito ospitante non sarà stato raggiunto. La fedeltà commerciale è
                    al 100% verso di te.
                </p>
            </div>

            <div className="space-y-3">
                <Toggle locked={locked} label="Host First abilitato" desc="Mantiene l'ordine di priorità: utente → sito → goal → lead." checked={hfp.enabled !== false} onChange={(v) => updNested("host_first_policy", "enabled", v)} testid="cfg-hfp-enabled" />
                <Toggle locked={locked} label="Goal first vincolante" desc="Blocca qualsiasi suggerimento Network prima del raggiungimento del goal." checked={hfp.enforce_goal_first !== false} onChange={(v) => updNested("host_first_policy", "enforce_goal_first", v)} testid="cfg-hfp-goal-first" />
                <Toggle locked={locked} label="Blocca concorrenti diretti" desc="Filtra automaticamente attività della tua stessa categoria dal Network." checked={hfp.block_competitors !== false} onChange={(v) => updNested("host_first_policy", "block_competitors", v)} testid="cfg-hfp-block-comp" />
                <Toggle locked={locked} label="Blocca brand esterni" desc="Aria non cita brand fuori dal Network Aria." checked={hfp.block_external_brands !== false} onChange={(v) => updNested("host_first_policy", "block_external_brands", v)} testid="cfg-hfp-block-brands" />
            </div>

            <div className="my-6 border-t border-white/5" />

            <div className="space-y-3">
                <Toggle locked={locked} label="Partecipa al Network Aria" desc="Dopo che il tuo goal è raggiunto, Aria può suggerire partner complementari (e tu lo stesso ad altri)." checked={!!cfg.ecosystem_enabled} onChange={(v) => upd("ecosystem_enabled", v)} testid="cfg-ecosystem-enabled" />
                <Toggle locked={locked} label="Accetta network suggestions dopo goal" desc="Permette ad Aria di proporre fino a N partner una volta soddisfatto il goal." checked={hfp.allow_network_after_goal !== false} onChange={(v) => updNested("host_first_policy", "allow_network_after_goal", v)} testid="cfg-hfp-after-goal" />
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-5">
                <Field label="Ruolo nell'ecosistema">
                    <select className="eco-input disabled:opacity-50 disabled:cursor-not-allowed" disabled={locked} value={cfg.ecosystem_role || "host"} onChange={(e) => upd("ecosystem_role", e.target.value)} data-testid="cfg-ecosystem-role">
                        <option value="host">Solo host — il mio sito riceve, non viene suggerito altrove</option>
                        <option value="partner">Partner Network — visibile e suggeribile ad altri Aria</option>
                        <option value="hidden">Hidden — esiste ma mai pubblico (modalità privata)</option>
                    </select>
                </Field>
                <Field label="Max suggerimenti partner per chat">
                    <input type="number" min="0" max="5" className="eco-input disabled:opacity-50 disabled:cursor-not-allowed" disabled={locked} value={hfp.max_network_suggestions ?? 2} onChange={(e) => updNested("host_first_policy", "max_network_suggestions", parseInt(e.target.value || "0", 10))} data-testid="cfg-hfp-max-net" />
                </Field>
            </div>
        </>
    );
}

const EXPERIENCE_LEVELS = [
    { key: "free", label: "Free", price: 0, icon: Sparkles, c1: "#9EC3FF", c2: "#4F8BFF", desc: "Pallino chat in fondo al sito + saluto gentile dopo 8 secondi. Gratis per sempre." },
    { key: "welcome", label: "Welcome", price: 19, icon: Rocket, c1: "#86EFAC", c2: "#10B981", desc: "Aria saluta da sola i visitatori appena arrivano, con un messaggio personalizzato." },
    { key: "full", label: "Full", price: 49, icon: Brain, c1: "#A855F7", c2: "#7C3AED", desc: "Aria diventa parte del sito — conosce prodotti e servizi, reagisce allo scroll." },
    { key: "cinematic", label: "Cinematic", price: 99, icon: Mic, c1: "#FF1A5C", c2: "#A855F7", desc: "Aria si manifesta da sola dopo pochi secondi di navigazione — volto, voce, presenza premium." },
];
const _MODES_BY_PLAN_FE = { free: ["free"], welcome: ["free", "welcome"], full: ["free", "welcome", "full"], pro: ["free", "welcome", "full"], cinematic: ["free", "welcome", "full", "cinematic"] };

function StepExperience({ cfg, token, onActivate, activating }) {
    const [copied, setCopied] = useState(false);
    const plan = cfg.plan_tier || "free";
    const mode = cfg.experience_mode || "free";
    const included = _MODES_BY_PLAN_FE[plan] || ["free"];
    const snippet = `<!-- Aria by TotalKlick - widget gratuito -->\n<script\n  src="https://totalklick.it/api/widget/aria.js"\n  data-token="${token}"\n  defer\n></script>\n<!-- /Aria -->`;

    const copySnippet = () => {
        navigator.clipboard.writeText(snippet);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <>
            <StepHeader n="07" title="Esperienza & Livello" subtitle="Dal testo semplice fino ad Aria che si manifesta da sola e parla — scegli cosa vedranno i tuoi visitatori." />

            <div className="grid sm:grid-cols-2 gap-3.5 mt-5" data-testid="cfg-experience-grid">
                {EXPERIENCE_LEVELS.map((lvl) => {
                    const isActive = mode === lvl.key;
                    const isIncluded = included.includes(lvl.key);
                    const Icon = lvl.icon;
                    const busy = activating === lvl.key;
                    return (
                        <div key={lvl.key} className="rounded-2xl p-4 relative" data-testid={`cfg-exp-card-${lvl.key}`}
                            style={{ background: "rgba(255,255,255,0.025)", border: `1px solid ${isActive ? lvl.c1 + "70" : "rgba(255,255,255,0.08)"}`, boxShadow: isActive ? `0 16px 40px -16px ${lvl.c1}55` : "none" }}>
                            {lvl.key === "cinematic" && (
                                <div className="absolute -top-2.5 right-3 text-[9.5px] px-2 py-0.5 rounded-full uppercase tracking-[0.18em]" style={{ background: `linear-gradient(135deg, ${lvl.c1}, ${lvl.c2})`, color: "#fff", fontFamily: "JetBrains Mono, monospace", fontWeight: 600 }}>
                                    premium
                                </div>
                            )}
                            <div className="flex items-center gap-2.5 mb-2">
                                <div className="w-8 h-8 rounded-lg grid place-items-center flex-none" style={{ background: `linear-gradient(135deg, ${lvl.c1}30, ${lvl.c2}10)`, border: `1px solid ${lvl.c1}40` }}>
                                    <Icon size={14} style={{ color: lvl.c1 }} />
                                </div>
                                <div>
                                    <div className="text-white text-[14px]" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>{lvl.label}</div>
                                    <div className="text-white/45 text-[11px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>{lvl.price === 0 ? "gratis" : `€${lvl.price}/mese`}</div>
                                </div>
                                {isActive && (
                                    <span className="ml-auto inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full text-emerald-300" style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.35)", fontFamily: "JetBrains Mono, monospace" }}>
                                        <Check size={9} /> attivo
                                    </span>
                                )}
                            </div>
                            <p className="text-white/60 text-[12px] leading-relaxed mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>{lvl.desc}</p>
                            <button
                                type="button"
                                disabled={isActive || busy}
                                onClick={() => onActivate(lvl.key)}
                                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-[12px] transition hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
                                style={isActive
                                    ? { background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.4)", border: "1px solid rgba(255,255,255,0.08)" }
                                    : { background: isIncluded ? `linear-gradient(135deg, ${lvl.c1}, ${lvl.c2})` : "transparent", color: isIncluded ? "#fff" : lvl.c1, border: isIncluded ? "none" : `1px solid ${lvl.c1}55`, fontWeight: 500 }}
                                data-testid={`cfg-exp-activate-${lvl.key}`}
                            >
                                {busy ? <Loader2 size={12} className="animate-spin" /> : (isIncluded ? null : <CreditCard size={12} />)}
                                {isActive ? "In uso" : busy ? "Un attimo…" : isIncluded ? "Attiva questo livello" : `Sblocca — €${lvl.price}/mese`}
                            </button>
                        </div>
                    );
                })}
            </div>

            {mode === "cinematic" && <VoicePicker cfg={cfg} token={token} onActivate={onActivate} activating={activating} />}

            <div className="my-7 border-t border-white/5" />

            <div className="grid md:grid-cols-2 gap-5 items-start">
                <div>
                    <div className="flex items-center gap-2 text-[#9EC3FF] text-[12px] mb-2" style={{ fontFamily: "JetBrains Mono, monospace", textTransform: "uppercase", letterSpacing: "0.18em" }}>
                        <Code2 size={13} /> Script da installare
                    </div>
                    <div className="relative rounded-xl overflow-hidden" style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.1)" }}>
                        <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
                            <span className="text-[10px] uppercase tracking-[0.18em] text-white/45" style={{ fontFamily: "JetBrains Mono, monospace" }}>html · identico per tutti i livelli</span>
                            <button onClick={copySnippet} type="button" className="inline-flex items-center gap-1.5 text-[11px] text-[#4F8BFF] hover:text-white transition" data-testid="cfg-exp-copy">
                                {copied ? <><Check size={11} /> Copiato!</> : <><Copy size={11} /> Copia</>}
                            </button>
                        </div>
                        <pre className="px-4 py-3 text-[11.5px] text-white/85 overflow-x-auto" style={{ fontFamily: "JetBrains Mono, monospace", lineHeight: 1.6 }}>{snippet}</pre>
                    </div>
                    <p className="text-white/40 text-[11.5px] mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                        Cambia solo cosa fa una volta installato — non serve toccarlo quando cambi livello.
                    </p>
                </div>
                <InstallGuideAnimation />
            </div>
        </>
    );
}

function VoicePicker({ cfg, token, onActivate, activating }) {
    const [voices, setVoices] = useState([]);
    const [loadingVoices, setLoadingVoices] = useState(true);
    const [previewing, setPreviewing] = useState(null);
    const audioRef = React.useRef(null);
    const selectedVoiceId = cfg.experience_config?.voice_id;

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get("/aria/tts/voices");
                setVoices(res.data?.voices || []);
            } catch (_e) { setVoices([]); }
            finally { setLoadingVoices(false); }
        })();
    }, []);

    const preview = async (voiceId) => {
        setPreviewing(voiceId);
        try {
            const res = await api.post("/aria/tts/speak", { text: `Ciao, sono ${cfg.persona_name || "Aria"}. Questa è la mia voce.`, voice_id: voiceId }, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            if (!audioRef.current) audioRef.current = new Audio();
            audioRef.current.src = url;
            audioRef.current.play().catch(() => {});
        } catch (_e) { /* voce non disponibile al momento */ }
        finally { setPreviewing(null); }
    };

    return (
        <div className="rounded-2xl p-4 mt-5" style={{ background: "rgba(255,26,92,0.05)", border: "1px solid rgba(255,26,92,0.2)" }} data-testid="cfg-voice-picker">
            <div className="flex items-center gap-2 text-[#FF8FA8] text-[12px] mb-3" style={{ fontFamily: "JetBrains Mono, monospace", textTransform: "uppercase", letterSpacing: "0.18em" }}>
                <Mic size={13} /> Voce di Aria (Cinematic)
            </div>
            {loadingVoices ? (
                <p className="text-white/45 text-[12px]">Carico le voci disponibili…</p>
            ) : (
                <>
                    <div className="grid sm:grid-cols-3 gap-2.5">
                        {voices.map((v) => {
                            const isSelected = selectedVoiceId ? selectedVoiceId === v.voice_id : voices[0]?.voice_id === v.voice_id;
                            return (
                                <div key={v.voice_id} className="rounded-xl p-3" style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${isSelected ? "rgba(255,26,92,0.5)" : "rgba(255,255,255,0.08)"}` }} data-testid={`cfg-voice-${v.voice_id}`}>
                                    <div className="text-white text-[13px] mb-2" style={{ fontFamily: "Geist, sans-serif" }}>{v.name}</div>
                                    <div className="flex items-center gap-2">
                                        <button type="button" onClick={() => preview(v.voice_id)} disabled={previewing === v.voice_id} className="text-[11px] text-[#9EC3FF] hover:text-white transition inline-flex items-center gap-1" data-testid={`cfg-voice-preview-${v.voice_id}`}>
                                            {previewing === v.voice_id ? <Loader2 size={10} className="animate-spin" /> : <Mic size={10} />} prova
                                        </button>
                                        <button
                                            type="button"
                                            disabled={isSelected || activating === "cinematic"}
                                            onClick={() => onActivate("cinematic", { voice_id: v.voice_id })}
                                            className="ml-auto text-[10.5px] px-2.5 py-1 rounded-full transition disabled:opacity-50"
                                            style={{ background: isSelected ? "rgba(16,185,129,0.14)" : "rgba(255,26,92,0.16)", color: isSelected ? "#6EE7B7" : "#FF8FA8", border: `1px solid ${isSelected ? "rgba(16,185,129,0.35)" : "rgba(255,26,92,0.35)"}` }}
                                            data-testid={`cfg-voice-select-${v.voice_id}`}
                                        >
                                            {isSelected ? "in uso" : "usa questa"}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {voices.length < 2 && (
                        <p className="text-white/35 text-[11px] mt-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                            Oggi è disponibile una sola voce — con un piano ElevenLabs superiore se ne sbloccano altre, qui compariranno in automatico.
                        </p>
                    )}
                </>
            )}
        </div>
    );
}

function InstallGuideAnimation() {
    const steps = [
        { Icon: Copy, label: "Copia lo script" },
        { Icon: MousePointerClick, label: "Apri l'editor del sito" },
        { Icon: Code2, label: "Incolla prima di </body>" },
        { Icon: FileCheck2, label: "Salva e pubblica" },
    ];
    const cycle = 4.4;
    return (
        <div className="rounded-2xl p-5" style={{ background: "rgba(79,139,255,0.05)", border: "1px solid rgba(79,139,255,0.18)" }} data-testid="cfg-install-guide">
            <div className="text-[10.5px] uppercase tracking-[0.2em] text-white/45 mb-4" style={{ fontFamily: "JetBrains Mono, monospace" }}>come si installa</div>
            <div className="relative flex items-center justify-between px-1">
                <div className="absolute left-5 right-5 top-5 h-px" style={{ background: "rgba(255,255,255,0.1)" }} />
                <motion.div
                    className="absolute top-3 w-4 h-4 rounded-full"
                    style={{ background: "radial-gradient(circle, #9EC3FF, rgba(158,195,255,0))" }}
                    animate={{ left: ["4%", "4%", "34%", "34%", "64%", "64%", "94%", "94%", "4%"] }}
                    transition={{ duration: cycle, repeat: Infinity, ease: "easeInOut", times: [0, 0.05, 0.22, 0.27, 0.47, 0.52, 0.72, 0.77, 1] }}
                />
                {steps.map(({ Icon, label }, i) => (
                    <div key={i} className="relative z-10 flex flex-col items-center gap-2 w-1/4">
                        <motion.div
                            className="w-10 h-10 rounded-full grid place-items-center"
                            style={{ background: "rgba(15,18,36,0.9)", border: "1px solid rgba(79,139,255,0.35)" }}
                            animate={{ scale: [1, 1, 1.22, 1.22, 1], boxShadow: ["0 0 0 rgba(79,139,255,0)", "0 0 0 rgba(79,139,255,0)", "0 0 22px rgba(79,139,255,0.55)", "0 0 22px rgba(79,139,255,0.55)", "0 0 0 rgba(79,139,255,0)"] }}
                            transition={{ duration: cycle, repeat: Infinity, ease: "easeInOut", delay: i * (cycle / 4), times: [0, 0.03, 0.1, 0.22, 0.3] }}
                        >
                            <Icon size={15} className="text-[#9EC3FF]" />
                        </motion.div>
                        <span className="text-[11px] text-white/70 text-center leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>{label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}


// ============== HELPERS UI ==============

function StepHeader({ n, title, subtitle }) {
    return (
        <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/40 mb-1.5" style={{ fontFamily: "JetBrains Mono, monospace" }}>step · {n}</div>
            <h2 className="text-white text-[22px] md:text-[26px]" style={{ fontFamily: "Geist, sans-serif", fontWeight: 300, letterSpacing: "-0.02em" }}>{title}</h2>
            <p className="text-white/55 mt-1 text-[13px]" style={{ fontFamily: "Outfit, sans-serif" }}>{subtitle}</p>
        </div>
    );
}

function Field({ label, children, full, required }) {
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <label className="block text-[10.5px] uppercase tracking-[0.18em] font-bold mb-1.5 text-white/50" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                {label}
                {required && <span className="text-[#FF7A88] ml-1">*</span>}
            </label>
            {children}
        </div>
    );
}

function Toggle({ label, desc, checked, onChange, testid, locked }) {
    return (
        <label className={`flex items-start gap-3 rounded-xl p-3 ${locked ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`} style={{ background: "rgba(255,255,255,0.02)", border: locked ? "1px solid rgba(255,170,40,0.18)" : "1px solid rgba(255,255,255,0.05)" }}>
            <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} disabled={!!locked} className="mt-0.5 w-4 h-4 disabled:cursor-not-allowed" style={{ accentColor: locked ? "#FFC36B" : "#4F8BFF" }} data-testid={testid} />
            <span className="flex-1">
                <span className="flex items-center gap-1.5 text-white text-[13px]" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                    {label}
                    {locked && <Lock size={10} className="text-[#FFC36B]" />}
                </span>
                <span className="block text-white/55 text-[12px] mt-0.5" style={{ fontFamily: "Outfit, sans-serif" }}>{desc}</span>
            </span>
        </label>
    );
}

function ChipList({ label, items, onChange, placeholder, testid, full }) {
    const [val, setVal] = useState("");
    const add = () => {
        const v = val.trim();
        if (!v) return;
        onChange([...(items || []), v]);
        setVal("");
    };
    const rm = (i) => onChange(items.filter((_, idx) => idx !== i));
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <label className="block text-[10.5px] uppercase tracking-[0.18em] font-bold mb-1.5 text-white/50" style={{ fontFamily: "JetBrains Mono, monospace" }}>{label}</label>
            <div className="flex gap-2 mb-2">
                <input className="eco-input flex-1" value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder={placeholder} data-testid={`${testid}-input`} />
                <button type="button" onClick={add} className="px-3 rounded-xl text-white" style={{ background: "rgba(79,139,255,0.18)", border: "1px solid rgba(79,139,255,0.4)" }} data-testid={`${testid}-add`}><Plus size={14} /></button>
            </div>
            {(items || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-list`}>
                    {items.map((it, i) => (
                        <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] text-white/85" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                            {it}
                            <button type="button" onClick={() => rm(i)} className="text-white/45 hover:text-white"><X size={11} /></button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

function NameDescriptionList({ label, items, onChange, testid }) {
    const add = () => onChange([...(items || []), { name: "", description: "", price: "" }]);
    const upd = (i, key, v) => onChange(items.map((x, idx) => (idx === i ? { ...x, [key]: v } : x)));
    const rm = (i) => onChange(items.filter((_, idx) => idx !== i));
    return (
        <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-1.5">
                <label className="block text-[10.5px] uppercase tracking-[0.18em] font-bold text-white/50" style={{ fontFamily: "JetBrains Mono, monospace" }}>{label}</label>
                <button type="button" onClick={add} className="text-[11.5px] text-[#4F8BFF] hover:text-white inline-flex items-center gap-1" data-testid={`${testid}-add`}><Plus size={12} /> aggiungi</button>
            </div>
            <div className="space-y-2">
                {(items || []).length === 0 && <div className="text-white/35 text-[12px]" style={{ fontFamily: "Outfit, sans-serif" }}>Nessun elemento. Clicca &quot;aggiungi&quot; per inserirne.</div>}
                {(items || []).map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-start rounded-xl p-2.5" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
                        <input className="eco-input col-span-4" value={it.name || ""} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Nome" data-testid={`${testid}-${i}-name`} />
                        <input className="eco-input col-span-5" value={it.description || ""} onChange={(e) => upd(i, "description", e.target.value)} placeholder="Descrizione" data-testid={`${testid}-${i}-desc`} />
                        <input className="eco-input col-span-2" value={it.price || ""} onChange={(e) => upd(i, "price", e.target.value)} placeholder="Prezzo" data-testid={`${testid}-${i}-price`} />
                        <button type="button" onClick={() => rm(i)} className="col-span-1 text-white/45 hover:text-rose-400 px-2 py-2"><X size={14} /></button>
                    </div>
                ))}
            </div>
        </div>
    );
}

function FaqList({ label, items, onChange, testid }) {
    const add = () => onChange([...(items || []), { q: "", a: "" }]);
    const upd = (i, key, v) => onChange(items.map((x, idx) => (idx === i ? { ...x, [key]: v } : x)));
    const rm = (i) => onChange(items.filter((_, idx) => idx !== i));
    return (
        <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-1.5">
                <label className="block text-[10.5px] uppercase tracking-[0.18em] font-bold text-white/50" style={{ fontFamily: "JetBrains Mono, monospace" }}>{label}</label>
                <button type="button" onClick={add} className="text-[11.5px] text-[#4F8BFF] hover:text-white inline-flex items-center gap-1" data-testid={`${testid}-add`}><Plus size={12} /> aggiungi FAQ</button>
            </div>
            <div className="space-y-2">
                {(items || []).map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-start rounded-xl p-2.5" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
                        <input className="eco-input col-span-5" value={it.q || ""} onChange={(e) => upd(i, "q", e.target.value)} placeholder="Domanda" data-testid={`${testid}-${i}-q`} />
                        <input className="eco-input col-span-6" value={it.a || ""} onChange={(e) => upd(i, "a", e.target.value)} placeholder="Risposta" data-testid={`${testid}-${i}-a`} />
                        <button type="button" onClick={() => rm(i)} className="col-span-1 text-white/45 hover:text-rose-400 px-2 py-2"><X size={14} /></button>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ErrBanner({ msg }) {
    return (
        <div className="mb-4 px-3 py-2.5 rounded-lg text-[13px]" style={{ background: "rgba(244,63,94,0.1)", border: "1px solid rgba(244,63,94,0.3)", color: "#FCA5A5" }} data-testid="aria-config-error">{msg}</div>
    );
}

// ============== NORMALIZERS ==============
function normalizeKB(v) {
    return {
        company_description: v?.company_description || "",
        services: v?.services || [],
        products: v?.products || [],
        faq: v?.faq || [],
        opening_hours: v?.opening_hours || "",
        contacts: v?.contacts || {},
        keywords: v?.keywords || [],
    };
}

// Fonde i risultati di "Compila dal sito" nello stato LOCALE (che può avere modifiche non ancora salvate):
// riempie solo gli scalari ancora vuoti e aggiunge solo le voci nuove per nome — non sovrascrive mai nulla.
function mergeKbFillOnly(localKb, serverKb) {
    const kb = normalizeKB(localKb);
    const sv = normalizeKB(serverKb);

    if (!kb.company_description.trim() && sv.company_description.trim()) kb.company_description = sv.company_description;
    if (!kb.opening_hours.trim() && sv.opening_hours.trim()) kb.opening_hours = sv.opening_hours;

    const contacts = { ...kb.contacts };
    for (const [k, v] of Object.entries(sv.contacts || {})) {
        if (v && !String(contacts[k] || "").trim()) contacts[k] = v;
    }
    kb.contacts = contacts;

    for (const listKey of ["services", "products"]) {
        const existing = [...kb[listKey]];
        const existingNames = new Set(existing.map((it) => String(it?.name || "").trim().toLowerCase()).filter(Boolean));
        for (const item of sv[listKey] || []) {
            const name = String(item?.name || "").trim();
            if (name && !existingNames.has(name.toLowerCase())) {
                existing.push(item);
                existingNames.add(name.toLowerCase());
            }
        }
        kb[listKey] = existing;
    }

    const existingKw = new Set(kb.keywords.map((k) => String(k).trim().toLowerCase()).filter(Boolean));
    const keywords = [...kb.keywords];
    for (const kw of sv.keywords || []) {
        const clean = String(kw).trim();
        if (clean && !existingKw.has(clean.toLowerCase())) {
            keywords.push(clean);
            existingKw.add(clean.toLowerCase());
        }
    }
    kb.keywords = keywords;

    return kb;
}

function normalizeCI(v) {
    return {
        tone_of_voice: v?.tone_of_voice || "professionale",
        operating_area: v?.operating_area || "",
        priority_products: v?.priority_products || [],
        avoid_products: v?.avoid_products || [],
        commercial_priorities: v?.commercial_priorities || "",
        must_ask: v?.must_ask || [],
        must_avoid: v?.must_avoid || [],
    };
}
function normalizeGoal(v) {
    return {
        type: v?.type || "lead",
        cta_label: v?.cta_label || "Lascia i tuoi contatti",
        cta_url: v?.cta_url || "",
        success_message: v?.success_message || "Perfetto, ti ricontatto a breve.",
        description: v?.description || "",
        completion_criteria: v?.completion_criteria || [],
        success_value_signals: v?.success_value_signals || [],
        fallback_action: v?.fallback_action || "lead_capture",
        kpis: v?.kpis || {},
    };
}
function normalizeGeo(v) {
    return {
        city: v?.city || "",
        province: v?.province || "",
        region: v?.region || "",
        country: v?.country || "IT",
        lat: v?.lat ?? null,
        lon: v?.lon ?? null,
        radius_km: v?.radius_km ?? 10,
        timezone: v?.timezone || "Europe/Rome",
    };
}
function normalizeBP(v) {
    return {
        category: v?.category || "",
        sub_category: v?.sub_category || "",
        target_audience: v?.target_audience || "",
        unique_value_proposition: v?.unique_value_proposition || "",
        price_range: v?.price_range || "",
        languages: v?.languages || ["it"],
        competitors_to_avoid: v?.competitors_to_avoid || [],
        phone: v?.phone || "",
    };
}
function normalizeHFP(v) {
    return {
        enabled: v?.enabled !== false,
        enforce_goal_first: v?.enforce_goal_first !== false,
        block_competitors: v?.block_competitors !== false,
        block_external_brands: v?.block_external_brands !== false,
        allow_network_after_goal: v?.allow_network_after_goal !== false,
        max_network_suggestions: v?.max_network_suggestions ?? 2,
    };
}

const baseStyle = { background: "#05050F", color: "#fff", fontFamily: "Geist, Outfit, sans-serif" };
const panelStyle = {
    background: "rgba(15,18,36,0.6)",
    border: "1px solid rgba(255,255,255,0.06)",
    backdropFilter: "blur(20px) saturate(140%)",
    WebkitBackdropFilter: "blur(20px) saturate(140%)",
    boxShadow: "0 30px 80px -20px rgba(0,0,0,0.5)",
};

const inputCSS = `
.eco-input{width:100%;padding:11px 13px;border-radius:10px;border:1px solid rgba(255,255,255,0.1);background:rgba(255,255,255,0.03);color:#fff;font-family:'Geist','Outfit',sans-serif;font-size:13.5px;transition:all .2s;}
.eco-input:focus{outline:none;border-color:rgba(79,139,255,0.55);background:rgba(255,255,255,0.05);box-shadow:0 0 0 3px rgba(79,139,255,0.1);}
.eco-input::placeholder{color:rgba(255,255,255,0.3);}
.eco-input option{background:#0F1224;color:#fff;}
textarea.eco-input{resize:vertical;}
`;
